import type { BubbleMenuPluginProps } from "@tiptap/extension-bubble-menu"
import { BubbleMenu } from "@tiptap/react/menus"
import type { Editor } from "@tiptap/react"
import { useCallback, useEffect } from "react"
import { LuLink } from "react-icons/lu"
import { ensureSafeLinkHref } from "./link-utils"
import {
  RichTextEditor as ChakraRichTextEditor,
  Control
} from "../chakra/rich-text-editor"
import type { EditorChain, NormalizedToolbarOption } from "./types"

const BUBBLE_MENU_VIEWPORT_PADDING = 8

const bubbleMenuFloatingOptions: NonNullable<BubbleMenuPluginProps["options"]> =
  {
    placement: "top" as const,
    offset: BUBBLE_MENU_VIEWPORT_PADDING,
    shift: {
      padding: BUBBLE_MENU_VIEWPORT_PADDING,
      rootBoundary: "viewport" as const
    },
    flip: {
      padding: BUBBLE_MENU_VIEWPORT_PADDING,
      rootBoundary: "viewport" as const
    },
    size: {
      padding: BUBBLE_MENU_VIEWPORT_PADDING,
      rootBoundary: "viewport" as const,
      apply({ availableWidth, elements }) {
        elements.floating.style.maxWidth = `${Math.max(
          0,
          Math.floor(availableWidth)
        )}px`
      }
    }
  }

export function RichTextEditorBubbleToolbar({
  editor,
  bubbleToolbarOptions
}: {
  editor: Editor
  bubbleToolbarOptions: NormalizedToolbarOption[]
}) {
  const handleLinkShortcut = useCallback(() => {
    const currentHref = editor.getAttributes("link")?.href || ""
    const value = window.prompt("Enter URL", currentHref)
    if (value === null) return
    const href = ensureSafeLinkHref(value)
    if (!href) {
      ;(editor.chain() as EditorChain)
        .focus()
        .extendMarkRange("link")
        .unsetLink()
        .run()
      return
    }
    ;(editor.chain() as EditorChain)
      .focus()
      .extendMarkRange("link")
      .setLink({ href })
      .run()
  }, [editor])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault()
        handleLinkShortcut()
      }
    }
    const target = editor.view.dom
    target.addEventListener("keydown", onKeyDown)
    return () => target.removeEventListener("keydown", onKeyDown)
  }, [editor, handleLinkShortcut])

  const renderToolbarControl = (
    option: NormalizedToolbarOption,
    index: number
  ) => {
    const key = `${option}-${index}`
    switch (option) {
      case "codeBlock":
        return <Control.CodeBlock key={key} />
      case "inlineCode":
        return <Control.Code key={key} />
      case "textLevel":
        return <Control.TextStyle key={key} />
      case "bold":
        return <Control.Bold key={key} />
      case "italic":
        return <Control.Italic key={key} />
      case "strikethrough":
        return <Control.Strikethrough key={key} />
      case "unorderedList":
        return <Control.BulletList key={key} />
      case "orderedList":
        return <Control.OrderedList key={key} />
      case "indentList":
        return <Control.IndentList key={key} />
      case "outdentList":
        return <Control.OutdentList key={key} />
      case "link":
        return (
          <Control.ButtonControl
            key={key}
            label="Link"
            icon={<LuLink />}
            variant={editor.isActive("link") ? "subtle" : "ghost"}
            onClick={handleLinkShortcut}
          />
        )
      case "image":
      case "attachment":
      case "embed":
        return null
      case "textColor":
        return <Control.TextColor key={key} />
      case "highlight":
        return <Control.Highlight key={key} />
      case "subscript":
        return <Control.Subscript key={key} />
      case "superscript":
        return <Control.Superscript key={key} />
      default:
        return null
    }
  }

  if (!bubbleToolbarOptions.length) return null

  return (
    <BubbleMenu editor={editor} options={bubbleMenuFloatingOptions}>
      <ChakraRichTextEditor.Toolbar variant="floating">
        {bubbleToolbarOptions.map(renderToolbarControl)}
      </ChakraRichTextEditor.Toolbar>
    </BubbleMenu>
  )
}
